import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { green, blue } from "@mui/material/colors";
import React, { useContext, useState, useEffect, useRef, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from 'tss-react/mui';
import ConfirmationModal from "../ConfirmationModal";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DescriptionIcon from '@mui/icons-material/Description';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, alpha, Autocomplete, Stack, InputAdornment } from "@mui/material";

import { i18n } from "../../translate/i18n";

import { Typography, TextField } from "@mui/material";
import api from "../../services/api";

import { AuthContext } from "../../context/Auth/AuthContext";
import ToastError from "../../toast/error/toastError";

const useStyles = makeStyles()((theme) => {
	return {
		root: {
			display: "flex",
			flexWrap: "wrap",
		},

		dialogPaper: {
			borderRadius: 16,
			minWidth: 480,
			maxWidth: 600,
		},

		dialogTitle: {
			padding: '24px 24px 16px',
			background: theme.palette.mode === 'dark'
				? `linear-gradient(135deg, ${alpha(blue[600], 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`
				: `linear-gradient(135deg, ${alpha(blue[50], 0.8)} 0%, ${alpha(theme.palette.primary.light, 0.1)} 100%)`,
			borderBottom: `1px solid ${theme.palette.divider}`,
		},

		titleContainer: {
			display: 'flex',
			alignItems: 'center',
			gap: 12,
		},

		titleIcon: {
			color: theme.palette.success.main,
			fontSize: 28,
		},

		dialogContent: {
			marginTop: '25px',
			padding: '24px',
		},

		messageContainer: {
			marginBottom: 24,
			padding: 16,
			borderRadius: 12,
			backgroundColor: theme.palette.mode === 'dark'
				? alpha(theme.palette.info.main, 0.08)
				: alpha(theme.palette.info.light, 0.1),
			border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
		},

		textFieldContainer: {
			marginTop: 8,
		},

		textField: {
			'& .MuiOutlinedInput-root': {
				borderRadius: 12,
				'&:hover .MuiOutlinedInput-notchedOutline': {
					borderColor: theme.palette.primary.main,
				},
				'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
					borderWidth: 2,
				},
			},
			'& .MuiInputLabel-root': {
				fontWeight: 500,
			},
		},

		btnWrapper: {
			position: "relative",
			minWidth: 120,
		},

		buttonProgress: {
			color: green[500],
			position: "absolute",
			top: "50%",
			left: "50%",
			marginTop: -12,
			marginLeft: -12,
		},

		actionButtons: {
			padding: '16px 24px 24px',
			gap: 12,
		},

		cancelButton: {
			borderRadius: 10,
			textTransform: 'none',
			fontWeight: 600,
			padding: '10px 24px',
		},

		confirmButton: {
			borderRadius: 10,
			textTransform: 'none',
			fontWeight: 600,
			padding: '10px 24px',
			boxShadow: theme.palette.mode === 'dark' ? 'none' : '0 2px 8px rgba(0,0,0,0.15)',
			'&:hover': {
				boxShadow: theme.palette.mode === 'dark' ? 'none' : '0 4px 12px rgba(0,0,0,0.2)',
			},
		},

		labelWithIcon: {
			display: 'flex',
			alignItems: 'center',
			gap: 8,
			fontWeight: 500,
			marginBottom: 8,
			color: theme.palette.text.primary,
		},

		autocompleteField: {
			'& .MuiOutlinedInput-root': {
				borderRadius: 12,
				'&:hover .MuiOutlinedInput-notchedOutline': {
					borderColor: theme.palette.primary.main,
				},
				'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
					borderWidth: 2,
				},
			},
			'& .MuiInputLabel-root': {
				fontWeight: 500,
			},
		},

	}
});

// Bolinha colorida que representa visualmente uma causa.
// Inclui um anel sutil pra manter contraste mesmo com cores claras/escuras
// em qualquer tema.
const CauseColorDot = ({ color, size = 14, sx }) => (
	<Box
		sx={{
			width: size,
			height: size,
			borderRadius: '50%',
			backgroundColor: color || '#bdbdbd',
			boxShadow: (t) => `inset 0 0 0 1px ${alpha(t.palette.common.black, 0.15)}, 0 0 0 2px ${alpha(color || t.palette.divider, 0.18)}`,
			flexShrink: 0,
			display: 'inline-block',
			...sx,
		}}
	/>
);

// Template pré-preenchido em "Observações Adicionais" pra orientar o atendente
// a documentar o atendimento de forma padronizada. O atendente preenche os
// detalhes abaixo de cada rótulo; o conteúdo cru vai pro notepad gerado no
// backend ao finalizar o ticket.
const FINISHED_REASON_TEMPLATE = `📄 Defeito informado:


🔍 Defeito identificado:


🛠 Solução aplicada:
`;

const CAUSES_PAGE_SIZE = 20;

const ReasonForContact = ({ open, onClose, ticketId }) => {
	const [isLoading, setIsLoading] = useState(false);
	const [finishedReason, setFinishedReason] = useState(FINISHED_REASON_TEMPLATE);
	const [serviceDescription, setServiceDescription] = useState("");
	const [selectedCause, setSelectedCause] = useState(null);
	const [causes, setCauses] = useState([]);
	const [loadingCauses, setLoadingCauses] = useState(false);
	const [causesInput, setCausesInput] = useState("");
	const [causesSearch, setCausesSearch] = useState("");
	const [causesPage, setCausesPage] = useState(1);
	const [causesHasMore, setCausesHasMore] = useState(false);
	const [hasAnyCause, setHasAnyCause] = useState(false);
	const causesRequestRef = useRef(0);
	const history = useHistory();
	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const [confirmationTypeIsGroup, setConfirmationTypeIsGroup] = useState(false);
	const { user: loggedInUser } = useContext(AuthContext);
	const { classes } = useStyles();

	const fetchCauses = useCallback(async (search, pageNumber) => {
		const requestId = ++causesRequestRef.current;
		setLoadingCauses(true);
		try {
			const { data } = await api.get("/causes", {
				params: {
					searchParam: search,
					pageNumber,
					pageSize: CAUSES_PAGE_SIZE,
				},
			});
			if (requestId !== causesRequestRef.current) return;
			const incoming = data.causes || [];
			setCauses((prev) => {
				if (pageNumber === 1) return incoming;
				const seen = new Set(prev.map((c) => c.id));
				const merged = [...prev];
				incoming.forEach((c) => {
					if (!seen.has(c.id)) merged.push(c);
				});
				return merged;
			});
			setCausesHasMore(Boolean(data.hasMore));
			// "Existe alguma causa para essa empresa?" só pode ser respondido
			// por uma busca sem filtro — se houver termo de busca, não mexe no
			// flag para o campo não sumir quando a busca não casa com nada.
			if (pageNumber === 1 && !search) {
				setHasAnyCause((data.count || 0) > 0);
			}
		} catch (err) {
			if (requestId === causesRequestRef.current) ToastError(err);
		} finally {
			if (requestId === causesRequestRef.current) setLoadingCauses(false);
		}
	}, []);

	// Reabriu o modal: zera estado de busca/paginação. O fetch fica a cargo do efeito de busca abaixo.
	useEffect(() => {
		if (!open) return;
		setCausesInput("");
		setCausesSearch("");
		setCausesPage(1);
		setCauses([]);
		setCausesHasMore(false);
		setHasAnyCause(false);
		setFinishedReason((prev) => (prev && prev.trim() !== "" ? prev : FINISHED_REASON_TEMPLATE));
	}, [open]);

	// Debounce do que o usuário digita: 300ms sem novas teclas → vira o termo de busca
	useEffect(() => {
		if (!open) return;
		const handle = setTimeout(() => {
			setCausesSearch(causesInput.trim());
		}, 300);
		return () => clearTimeout(handle);
	}, [causesInput, open]);

	// Termo de busca (ou abertura do modal) muda → carrega a página 1
	useEffect(() => {
		if (!open) return;
		setCausesPage(1);
		fetchCauses(causesSearch, 1);
	}, [causesSearch, open, fetchCauses]);

	const handleCausesScroll = (event) => {
		if (loadingCauses || !causesHasMore) return;
		const node = event.currentTarget;
		const threshold = 48;
		if (node.scrollTop + node.clientHeight >= node.scrollHeight - threshold) {
			const nextPage = causesPage + 1;
			setCausesPage(nextPage);
			fetchCauses(causesSearch, nextPage);
		}
	};

	const handleClose = () => {
		setFinishedReason(FINISHED_REASON_TEMPLATE);
		setServiceDescription("");
		setSelectedCause(null);
		onClose();
	};

	const handleCloseTicket = async () => {
		setIsLoading(true)
		try {
			await api.put(`/tickets/${ticketId}`, {
				status: 'closed',
				userId: loggedInUser.id,
				reason: '',
				wasResolved: 1,
				force: false,
				finishedReason: finishedReason || 'Finalizado pelo usuário',
				serviceDescription: serviceDescription.trim(),
				causeId: selectedCause?.id || null
			});
			history.push("/tickets");
		} catch (err) {
			const errorMsg = err.response?.data?.error;
			console.error(errorMsg)
			if (errorMsg == "ERR_SENDING_WAPP_MSG_GROUP_FORBIDDEN" || errorMsg == "ERR_SENDING_WAPP_MSG_USER_FORBIDDEN") {
				setConfirmationTypeIsGroup(`${errorMsg === "ERR_SENDING_WAPP_MSG_GROUP_FORBIDDEN" ? true : false} `)
				setTimeout(() => setConfirmationOpen(true), 100);
			}
			ToastError(err);
		}
		setIsLoading(false)
		handleClose();
	};

	const handleCloseTicketForce = async () => {
		setIsLoading(true)
		try {
			await api.put(`/tickets/${ticketId}`, {
				status: 'closed',
				userId: loggedInUser.id,
				reason: '',
				wasResolved: 1,
				force: true,
				finishedReason: finishedReason || 'Finalizado pelo usuário',
				serviceDescription: serviceDescription.trim(),
				causeId: selectedCause?.id || null
			});
			history.push("/tickets");
		} catch (err) {
			const errorMsg = err.response?.data?.error;
			console.error(errorMsg)
			ToastError(err);
		}
		setIsLoading(false)
		handleClose();
	};

	return (
		<div className={classes.root}>
			<ConfirmationModal
				title={`${confirmationTypeIsGroup ? i18n.t(`ticketOptionsMenu.confirmationModalForce.titleGroup`) : i18n.t(`ticketOptionsMenu.confirmationModalForce.titleUser`)} `}
				open={confirmationOpen}
				onClose={setConfirmationOpen}
				onConfirm={handleCloseTicketForce}
				force={true}
			>
				{i18n.t("ticketOptionsMenu.confirmationModalForce.message")}
			</ConfirmationModal>
			<Dialog
				open={open}
				onClose={handleClose}
				scroll="body"
				maxWidth="sm"
				fullWidth
				PaperProps={{
					className: classes.dialogPaper,
					elevation: 12
				}}
				slotProps={{
					backdrop: {
						style: {
							backdropFilter: 'blur(8px)',
							backgroundColor: 'rgba(0, 0, 50, 0.4)',
						},
					},
				}}
			>
				<DialogTitle className={classes.dialogTitle}>
					<Box className={classes.titleContainer}>
						<CheckCircleIcon className={classes.titleIcon} />
						<Typography variant="h5" component="h2" fontWeight={600}>
							Finalizar Atendimento
						</Typography>
					</Box>
				</DialogTitle>

				<DialogContent className={classes.dialogContent}>
					<Box className={classes.messageContainer}>
						<Typography variant="body1" color="text.primary" fontWeight={500} >
							🎯 Você está prestes a finalizar este atendimento
						</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
							Esta ação marcará o ticket como resolvido e o atendimento será encerrado.
						</Typography>
					</Box>
					{/* Campo de seleção de causa - só aparece se a empresa tiver pelo menos uma causa cadastrada */}
					{+loggedInUser.companyId === 2 && hasAnyCause && (
						<Box className={classes.textFieldContainer} sx={{ mb: 3 }}>
							<Box className={classes.labelWithIcon}>
								<HelpOutlineIcon fontSize="small" color="primary" />
								<Typography variant="body2">
									Causa da Finalização *
								</Typography>
							</Box>
							<Autocomplete
								size="small"
								options={causes}
								getOptionLabel={(option) => option.name || ''}
								isOptionEqualToValue={(option, value) => option?.id === value?.id}
								value={selectedCause}
								onChange={(_event, newValue) => {
									setSelectedCause(newValue);
								}}
								inputValue={causesInput}
								onInputChange={(_event, newInputValue, reason) => {
									if (reason === 'reset') return;
									setCausesInput(newInputValue);
								}}
								filterOptions={(opts) => opts}
								loading={loadingCauses}
								ListboxProps={{ onScroll: handleCausesScroll, style: { maxHeight: 320 } }}
								renderInput={(params) => (
									<TextField
										{...params}
										size="small"
										placeholder="Selecione uma causa para a finalização..."
										variant="outlined"
										className={classes.autocompleteField}
										required
										error={!selectedCause}
										helperText={!selectedCause ? "Selecione uma causa para continuar" : ""}
										InputProps={{
											...params.InputProps,
											startAdornment: selectedCause ? (
												<InputAdornment position="start" sx={{ ml: 0.5 }}>
													<CauseColorDot color={selectedCause.color} size={14} />
												</InputAdornment>
											) : null,
											endAdornment: (
												<>
													{loadingCauses ? <CircularProgress color="inherit" size={16} /> : null}
													{params.InputProps.endAdornment}
												</>
											),
										}}
									/>
								)}
								renderOption={(props, option) => {
									const { key, ...rest } = props;
									return (
										<Box
											component="li"
											key={key}
											{...rest}
											sx={{
												alignItems: 'flex-start !important',
												gap: 1.25,
												py: 1.25,
												px: 1.5,
												'&:hover': {
													bgcolor: (t) => alpha(option.color || t.palette.primary.main, 0.08),
												},
												'&[aria-selected="true"]': {
													bgcolor: (t) => alpha(option.color || t.palette.primary.main, 0.15),
												},
											}}
										>
											<CauseColorDot color={option.color} size={14} sx={{ mt: 0.5, flexShrink: 0 }} />
											<Box sx={{ minWidth: 0, flex: 1 }}>
												<Typography
													variant="body2"
													sx={{
														fontWeight: 600,
														color: 'text.primary',
														lineHeight: 1.3,
													}}
												>
													{option.name}
												</Typography>
												{option.description ? (
													<Typography
														variant="caption"
														sx={{
															display: '-webkit-box',
															WebkitLineClamp: 2,
															WebkitBoxOrient: 'vertical',
															overflow: 'hidden',
															textOverflow: 'ellipsis',
															color: 'text.secondary',
															lineHeight: 1.4,
															mt: 0.25,
															whiteSpace: 'normal',
														}}
													>
														{option.description}
													</Typography>
												) : null}
											</Box>
										</Box>
									);
								}}
								noOptionsText="Nenhuma causa encontrada"
								loadingText="Carregando causas..."
							/>

							{/* Painel de descrição da causa selecionada */}
							{selectedCause?.description ? (
								<Box
									sx={{
										mt: 1.25,
										p: 1.5,
										borderRadius: 1.5,
										display: 'flex',
										alignItems: 'flex-start',
										gap: 1.25,
										bgcolor: (t) => alpha(selectedCause.color || t.palette.info.main, 0.06),
										border: (t) => `1px solid ${alpha(selectedCause.color || t.palette.info.main, 0.25)}`,
									}}
								>
									<CauseColorDot color={selectedCause.color} size={14} sx={{ mt: 0.4, flexShrink: 0 }} />
									<Box sx={{ minWidth: 0 }}>
										<Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
											<Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary' }}>
												{selectedCause.name}
											</Typography>
										</Stack>
										<Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>
											{selectedCause.description}
										</Typography>
									</Box>
								</Box>
							) : (
								<Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
									🎯 Selecione a causa que melhor representa o motivo da finalização
								</Typography>
							)}
						</Box>
					)}

					{/* Descrição do atendimento - visível ao cliente, obrigatória */}
					{+loggedInUser.companyId === 2 && (
						<Box className={classes.textFieldContainer} sx={{ mb: 3 }}>
							<Box className={classes.labelWithIcon}>
								<DescriptionIcon fontSize="small" color="primary" />
								<Typography variant="body2">
									Descrição do atendimento *
								</Typography>
							</Box>
							<TextField
								fullWidth
								size="small"
								multiline
								rows={4}
								required
								placeholder="Descreva o atendimento de forma clara e profissional. Ex: Resolvido problema de acesso ao sistema, foi necessário redefinir senha do usuário."
								value={serviceDescription}
								onChange={(e) => setServiceDescription(e.target.value)}
								variant="outlined"
								className={classes.textField}
								error={!serviceDescription.trim()}
								helperText={!serviceDescription.trim() ? "Campo obrigatório" : ""}
								InputProps={{
									style: { fontSize: '14px', lineHeight: 1.5 }
								}}
							/>
							<Box
								sx={{
									mt: 1,
									p: 1.25,
									borderRadius: 1.5,
									display: 'flex',
									alignItems: 'flex-start',
									gap: 1,
									bgcolor: (t) => alpha(t.palette.warning.main, 0.08),
									border: (t) => `1px solid ${alpha(t.palette.warning.main, 0.25)}`,
								}}
							>
								<VisibilityIcon sx={{ fontSize: 18, color: 'warning.main', mt: '1px' }} />
								<Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.4 }}>
									<strong>Atenção:</strong> o cliente pode ter acesso a esta informação. Evite dados sensíveis e use uma linguagem profissional.
								</Typography>
							</Box>
						</Box>
					)}

					{/* Campo de observações - só aparece se companyId for 2 */}
					{+loggedInUser.companyId === 2 && (
						<Box className={classes.textFieldContainer}>
							<Box className={classes.labelWithIcon}>
								<EditIcon fontSize="small" color="primary" />
								<Typography variant="body2">
									Observações Adicionais (Opcional)
								</Typography>
							</Box>
							<TextField
								fullWidth
								size="small"
								multiline
								minRows={9}
								maxRows={16}
								placeholder={FINISHED_REASON_TEMPLATE}
								value={finishedReason}
								onChange={(e) => setFinishedReason(e.target.value)}
								variant="outlined"
								className={classes.textField}
								InputProps={{
									style: { fontSize: '14px', lineHeight: 1.5 }
								}}
							/>
							<Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
								💡 Preencha cada bloco abaixo dos rótulos. Esse conteúdo será adicionado ao notepad do ticket quando finalizar
							</Typography>
						</Box>
					)}
				</DialogContent>

				<DialogActions className={classes.actionButtons}>
					<Button
						size="small"
						onClick={handleClose}
						color="inherit"
						disabled={isLoading}
						variant="outlined"
						className={classes.cancelButton}
					>
						Cancelar
					</Button>
					<Button
						size="small"
						type="submit"
						color="success"
						disabled={
							isLoading ||
							(+loggedInUser.companyId === 2 && (
								(hasAnyCause && !selectedCause) ||
								!serviceDescription.trim()
							))
						}
						variant="contained"
						className={`${classes.btnWrapper} ${classes.confirmButton}`}
						onClick={handleCloseTicket}
						startIcon={!isLoading ? <CheckCircleIcon /> : null}
					>
						{isLoading ? 'Finalizando...' : 'Finalizar Atendimento'}
						{isLoading && (
							<CircularProgress
								size={16}
								className={classes.buttonProgress}
							/>
						)}
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};

export default ReasonForContact;
